import * as THREE from 'three';

/**
 * Procedural colour-grade LUTs.
 *
 * A 32³ cube unrolled into a 1024×32 strip, generated texel by texel in JS —
 * no .cube file, no image. The grade runs in display (gamma) space, which is
 * where colourists actually work and where 8 bits are perceptually enough.
 *
 * Every look is the same small stack: exposure/contrast pivot, a
 * shadow/highlight split-tone (the teal-shadow, warm-highlight separation that
 * makes a frame read as "film"), channel gain, then saturation with a
 * luminance-preserving mix.
 */

const SIZE = 32;

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const lum = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

/**
 * @typedef {object} Look
 * @property {number} contrast   S-curve strength around `pivot`
 * @property {number} pivot
 * @property {number} sat
 * @property {number[]} lift     added in shadows (rgb)
 * @property {number[]} gain     multiplied in highlights (rgb)
 * @property {number[]} shadowTint
 * @property {number[]} highTint
 * @property {number} shadowMix
 * @property {number} highMix
 * @property {number} crush      black point
 * @property {number} white      white point
 */

export const LOOKS = {
  neutral: { contrast: 1.06, pivot: 0.44, sat: 1.00, lift: [0, 0, 0], gain: [1, 1, 1],
             shadowTint: [0.42, 0.55, 0.72], highTint: [1.0, 0.97, 0.92], shadowMix: 0.10, highMix: 0.06,
             greenSat: 0.72, greenHue: 0.20,
             crush: 0.004, white: 1.0 },

  // The house grade: cool bruised shadows, warm sunlit highlights, punchy mids.
  //
  // `greenSat` is the important one. A grass battlefield is 50-60 % of every
  // wide frame, and at full chroma it becomes one unbroken slab that no amount
  // of lighting can break up. Pulling only the green wedge back — and rolling
  // what is left towards olive — is what lets the faction reds and blues stay
  // hot while the ground reads as dry summer grass under a warm key.
  battle:  { contrast: 1.32, pivot: 0.46, sat: 1.08, lift: [0.004, 0.004, 0.020], gain: [1.07, 0.985, 0.925],
             shadowTint: [0.19, 0.39, 0.94], highTint: [1.0, 0.885, 0.68], shadowMix: 0.52, highMix: 0.30,
             shadowRange: 1.24, greenSat: 0.70, greenHue: 0.22,
             crush: 0.0, white: 0.994, whiteRoll: 4.0, whiteKnee: 0.78 },

  // Musou: bloom-friendly, gold pushed hard, shadows go violet.
  musou:   { contrast: 1.34, pivot: 0.40, sat: 1.42, lift: [0.010, -0.006, 0.030], gain: [1.13, 1.02, 0.86],
             shadowTint: [0.40, 0.30, 0.86], highTint: [1.0, 0.86, 0.55], shadowMix: 0.34, highMix: 0.34,
             crush: 0.014, white: 0.995, whiteRoll: 4.6, whiteKnee: 0.74 },

  dusk:    { contrast: 1.26, pivot: 0.40, sat: 1.06, lift: [0.010, 0.000, 0.026], gain: [1.10, 0.975, 0.90],
             shadowTint: [0.30, 0.36, 0.80], highTint: [1.0, 0.80, 0.60], shadowMix: 0.38, highMix: 0.30,
             shadowRange: 1.35, greenSat: 0.58, greenHue: 0.30,
             crush: 0.004, white: 0.995 },

  storm:   { contrast: 1.22, pivot: 0.46, sat: 0.86, lift: [-0.004, 0.0, 0.014], gain: [0.95, 0.985, 1.06],
             shadowTint: [0.34, 0.44, 0.68], highTint: [0.88, 0.93, 1.0], shadowMix: 0.30, highMix: 0.18,
             crush: 0.010, white: 1.0 },

  night:   { contrast: 1.24, pivot: 0.36, sat: 0.92, lift: [-0.006, -0.002, 0.026], gain: [0.92, 0.96, 1.12],
             shadowTint: [0.20, 0.32, 0.86], highTint: [1.0, 0.86, 0.66], shadowMix: 0.44, highMix: 0.24,
             crush: 0.020, white: 0.99 },

  // Taking damage: desaturated, red-shifted, crushed.
  hurt:    { contrast: 1.30, pivot: 0.40, sat: 0.62, lift: [0.030, -0.010, -0.010], gain: [1.16, 0.80, 0.78],
             shadowTint: [0.70, 0.16, 0.14], highTint: [1.0, 0.62, 0.52], shadowMix: 0.42, highMix: 0.30,
             crush: 0.030, white: 0.97 },
};

function applyLook(L, r, g, b, out) {
  // black/white point
  const inv = 1 / Math.max(1e-3, L.white - L.crush);
  r = clamp01((r - L.crush) * inv);
  g = clamp01((g - L.crush) * inv);
  b = clamp01((b - L.crush) * inv);

  // Contrast about the pivot, in gamma space.
  //
  // A straight line `p + (v - p) * c` clips everything below `p - p/c` to pure
  // black: at battle's 1.26 / 0.44 that is every value under sRGB 0.09, i.e.
  // most of a low-sun frame's shadow side. This is the same slope at the pivot
  // but bends towards 0 and 1 instead of running into them, so shadow and
  // highlight detail survive the grade.
  const c = L.contrast, p = L.pivot;
  const con = (v) => (v <= 0 ? 0 : v >= 1 ? 1
    : v < p ? p * Math.pow(v / p, c)
            : 1 - (1 - p) * Math.pow((1 - v) / (1 - p), c));
  r = con(r); g = con(g); b = con(b);

  // Split tone: shadows one way, highlights the other. `shadowRange` below 2
  // walks the cool tint up out of the deep shadows and into the midtones —
  // without it a warm key produces no warm/cool separation on a big midtone
  // surface like a field, because the tint never reaches it.
  const y = lum(r, g, b);
  const sr = L.shadowRange !== undefined ? L.shadowRange : 2.0;
  const sw = Math.pow(1 - clamp01(y), sr) * L.shadowMix;
  const hw = Math.pow(clamp01(y), 2.0) * L.highMix;
  r = r * (1 - sw) + L.shadowTint[0] * y * sw * 1.6 + r * sw * 0.4;
  g = g * (1 - sw) + L.shadowTint[1] * y * sw * 1.6 + g * sw * 0.4;
  b = b * (1 - sw) + L.shadowTint[2] * y * sw * 1.6 + b * sw * 0.4;
  r = r * (1 - hw) + L.highTint[0] * hw * (0.35 + 0.65 * r) + r * hw * 0.0;
  g = g * (1 - hw) + L.highTint[1] * hw * (0.35 + 0.65 * g);
  b = b * (1 - hw) + L.highTint[2] * hw * (0.35 + 0.65 * b);

  // lift + gain
  r = clamp01(r * L.gain[0] + L.lift[0] * (1 - r));
  g = clamp01(g * L.gain[1] + L.lift[1] * (1 - g));
  b = clamp01(b * L.gain[2] + L.lift[2] * (1 - b));

  // hue-selective chroma: pull the green wedge back before the global lift
  if (L.greenSat !== undefined) {
    const mx = Math.max(r, Math.max(g, b)), mn = Math.min(r, Math.min(g, b));
    const chroma = mx - mn;
    if (chroma > 1e-4 && g >= r && g >= b) {
      // 1 on pure green, falling to 0 as the hue leaves the wedge
      const gw = clamp01((g - Math.max(r, b)) / chroma);
      const k = gw * (1 - L.greenSat);
      const yg = lum(r, g, b);
      r += (yg - r) * k * 0.55;
      g += (yg - g) * k;
      b += (yg - b) * k;
      // and roll the survivor towards olive rather than emerald
      const hshift = (L.greenHue || 0) * gw;
      r = r + (g - r) * hshift;
      b = b - b * hshift * 0.25;
    }
  }

  // saturation
  const y2 = lum(r, g, b);
  r = clamp01(y2 + (r - y2) * L.sat);
  g = clamp01(y2 + (g - y2) * L.sat);
  b = clamp01(y2 + (b - y2) * L.sat);

  // SHOULDER: converge on white at the very top, AFTER saturation so nothing
  // can pull the tint back in.
  //
  // Film desaturates as it clips -- a specular hotspot goes white however warm
  // the key is. This grade's highTint and gain instead carry their warmth all
  // the way up, so the top of the range is a warm cream and never neutral.
  // Measured on a golden frame: 4.11% of pixels cleared luminance 200, so the
  // image was NOT short of bright pixels, but the brightest 621 of them sat at
  // (253,244,211) and not one reached white in all three channels. That is why
  // armour and banner edges never read as hot, and why an additive spark could
  // not clip to a core no matter what `bright` it was given in vfx/defs.js --
  // its ceiling was here, in the grade, not in the effect.
  //
  // Driven by the MIN channel, which is the only one of the three that can
  // tell a specular from a bright wall. Max-channel cannot: this grade's gain
  // clips red for a wide band of inputs, so a sunlit curtain wall and a white
  // hotspot both read 1.0 there -- a knee at 0.88 on max-channel blew that
  // wall into a flat white slab with no courses and no warm stone, which is
  // the veiling-glare failure the bloom threshold above exists to avoid.
  // A warm diffuse surface keeps its blue LOW (that wall sits at min 0.75)
  // while anything genuinely near white does not (1.0 white sits at 0.83), so
  // a min-channel knee at 0.78 separates them cleanly. Measured across pale,
  // sunlit and bright wall, faction red and grass: not one shifts by a single
  // 1/255 step, while white still converges.
  // Driven by the MAX channel, not luminance. A warm highlight clips its red
  // long before its luminance approaches 1 -- at white in, this grade lands on
  // (1.0, 0.97, 0.89), whose luminance is only 0.971, so a luminance-driven
  // knee can never reach the top and closes barely half the gap. Max-channel
  // is also the physically right trigger: a specular is blowing out when ANY
  // channel is against the ceiling.
  const roll = L.whiteRoll || 0;
  if (roll > 0) {
    const knee = L.whiteKnee !== undefined ? L.whiteKnee : 0.80;
    const t = clamp01((Math.min(r, Math.min(g, b)) - knee) / (1 - knee));
    const k = clamp01(t * roll);
    if (k > 0) { r += (1 - r) * k; g += (1 - g) * k; b += (1 - b) * k; }
  }

  out[0] = clamp01(r); out[1] = clamp01(g); out[2] = clamp01(b);
  return out;
}

const _o = [0, 0, 0];

/** Build the 1024×32 strip texture for one look. */
export function buildLUT(look) {
  const L = typeof look === 'string' ? (LOOKS[look] || LOOKS.neutral) : look;
  const W = SIZE * SIZE, H = SIZE;
  const data = new Uint8Array(W * H * 4);
  const inv = 1 / (SIZE - 1);
  for (let bI = 0; bI < SIZE; bI++) {
    const b = bI * inv;
    for (let gI = 0; gI < SIZE; gI++) {
      const g = gI * inv;
      const row = gI * W;
      for (let rI = 0; rI < SIZE; rI++) {
        const r = rI * inv;
        applyLook(L, r, g, b, _o);
        const k = (row + bI * SIZE + rI) * 4;
        data[k] = (_o[0] * 255 + 0.5) | 0;
        data[k + 1] = (_o[1] * 255 + 0.5) | 0;
        data[k + 2] = (_o[2] * 255 + 0.5) | 0;
        data[k + 3] = 255;
      }
    }
  }
  const tex = new THREE.DataTexture(data, W, H, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/** GLSL side of the strip lookup — shared by the composite pass. */
export const LUT_GLSL = /* glsl */`
  vec3 sampleLUT(sampler2D tex, vec3 c) {
    c = clamp(c, 0.0, 1.0);
    const float N = 32.0;
    float sliceW = 1.0 / N;                 // width of one slice in u
    float texelW = sliceW / N;
    float innerW = texelW * (N - 1.0);
    float z = c.b * (N - 1.0);
    float z0 = floor(z), z1 = min(z0 + 1.0, N - 1.0);
    float xo = texelW * 0.5 + c.r * innerW;
    float v = (c.g * (N - 1.0) + 0.5) / N;
    vec3 a = texture2D(tex, vec2(z0 * sliceW + xo, v)).rgb;
    vec3 b = texture2D(tex, vec2(z1 * sliceW + xo, v)).rgb;
    return mix(a, b, z - z0);
  }`;
